// Duo Universal Prompt second factor (apps/pipeline_api/src/duo.rs; docs/pipeline/V1-CONTRACT.md
// "Second factor: Duo"). The server builds the signed Duo request; the browser only navigates to it.
// Duo sends the browser back to /api/auth/mfa/duo/callback, which redirects to "/" when approved, or
// to "/?duo_error=<code>" with the session still waiting for a second factor.
import { api, ApiError } from "./api";

export type DuoErrorCode = "denied" | "not_enrolled" | "unavailable" | "timed_out" | "failed" | "disabled";

export type DuoEnrollment =
  | { delivery: "email"; email: string; expiration?: number }
  | { delivery: "activation"; email: string; activation_barcode: string; activation_code: string; expiration?: number };

/** Starts a Duo check: the server health-checks Duo, then we go to Duo's prompt. */
export async function startDuo(go: (url: string) => void = (u) => window.location.assign(u)): Promise<void> {
  const { url } = await api<{ url: string }>("/api/auth/mfa/duo/start", { method: "POST", body: {} });
  go(url);
}

/** Requests a Duo setup email (or activation QR/link) for the current signed-in user on first login. */
export async function requestDuoSetup(): Promise<DuoEnrollment> {
  return api<DuoEnrollment>("/api/auth/mfa/duo/enroll", { method: "POST", body: {} });
}

/** Superadmin: sends a Duo setup email (or activation link) for a staff user. */
export async function sendAdminDuoSetup(userId: string): Promise<DuoEnrollment> {
  return api<DuoEnrollment>(`/api/admin/users/${userId}/duo-enroll`, { method: "POST", body: {} });
}

/** A plain-language message for a Duo failure (the callback's `duo_error`, or a start error). */
export function duoErrorMessage(code: string): string {
  switch (code) {
    case "denied":
      return "Duo didn't approve this sign-in. Try again, or use a passkey.";
    case "not_enrolled":
      return "You aren't enrolled in Duo yet. Choose Send Duo setup email below to activate Duo Mobile on your phone, or ask an administrator to enroll you in Duo.";
    case "unavailable":
    case "duo_unavailable":
      return "Duo is unavailable right now. Try again in a few minutes, or use a passkey.";
    case "timed_out":
      return "The Duo request timed out. Try again.";
    case "disabled":
      return "Your account is disabled.";
    case "duo_not_available":
      return "Duo needs your verified Microsoft sign-in. Sign out and sign in again, or use a passkey.";
    case "duo_not_configured":
      return "Duo isn't set up for LoanShip. Use a passkey.";
    case "duo_already_enrolled":
      return "You're already set up in Duo. Choose Approve on my phone (Duo) to sign in.";
    case "duo_enroll_not_configured":
      return "Duo setup emails aren't enabled on this server yet. Ask an administrator to enroll you in Duo, or use a passkey.";
    default:
      return "Duo couldn't confirm your sign-in. Try again.";
  }
}

/** A start failure as a message. */
export function duoStartError(e: unknown): string {
  return e instanceof ApiError ? duoErrorMessage(e.code) : "Duo couldn't start. Try again.";
}

/** A Duo setup / enrollment request failure as a message. */
export function duoEnrollError(e: unknown): string {
  return e instanceof ApiError ? duoErrorMessage(e.code) : "Couldn't send the Duo setup email. Try again.";
}

/** Reads `duo_error` from the address and removes it, so a reload doesn't show it again. */
export function takeDuoError(loc: Location = window.location, history: History = window.history): string | null {
  const params = new URLSearchParams(loc.search);
  const code = params.get("duo_error");
  if (!code) return null;
  params.delete("duo_error");
  const qs = params.toString();
  history.replaceState(null, "", loc.pathname + (qs ? `?${qs}` : "") + loc.hash);
  return code;
}
